import { cryptaniumVaultContractConfig } from "@config/contractsConfig";
import { useMemo } from "react";
import { formatUnits } from "viem";
import { useAccount, useReadContracts } from "wagmi";
import useDappChain from "./useDappChain";

const useCryptaniumVaultDepositLimit = () => {
    const { chain } = useDappChain();
    const { address: walletAddress } = useAccount();

    const depositLimitContracts = useMemo(() => {
        const vaultConfig = cryptaniumVaultContractConfig[chain.id]?.interactionPool;
        const contracts = [];

        if (!vaultConfig) {
            return [];
        }

        contracts.push({
            ...vaultConfig,
            functionName: "maxDeposit",
            args: [walletAddress],
        });

        contracts.push({
            ...vaultConfig,
            functionName: "totalAssets",
        });

        return contracts;
    }, [chain.id, walletAddress]);

    const { data } = useReadContracts({
        contracts: depositLimitContracts,
        query: {
            enabled: !!walletAddress && depositLimitContracts.length > 0,
            refetchInterval: 10_000,
        },
    });

    const { depositLimit, maxDeposit } = useMemo(() => {
        if (!data || !data[0]?.result || !data[1]?.result) {
            return {
                depositLimit: 1000000,
                maxDeposit: 1000000,
            };
        }

        const maxDepositResult = (data[0].result as bigint) || 0n;
        const totalAssetsResult = (data[1].result as bigint) || 0n;

        const formattedMaxDeposit = Number(formatUnits(maxDepositResult, 18));
        const formattedTotalAssets = Number(formatUnits(totalAssetsResult, 18));

        const calculatedDepositLimit = Math.max(
            0,
            formattedMaxDeposit - formattedTotalAssets
        );

        return {
            depositLimit: calculatedDepositLimit || 1000000,
            maxDeposit: formattedMaxDeposit || 1000000,
        };
    }, [data]);

    return {
        depositLimit,
        maxDeposit,
    };
};

export default useCryptaniumVaultDepositLimit;